"use client";

import React, { useState } from "react";
import { Check, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const plans = [{
  name: "Essential Wellness",
  priceMonthly: 199,
  priceAnnual: 159,
  description: "The foundation for your longevity journey and routine care.",
  features: ["1 Monthly Vitamin Injection", "10% Off All IV Therapy", "Priority Scheduling", "Member-Only Lab Pricing", "Wellness Room Access"],
  notIncluded: ["Unlimited Sauna Access", "Colon Hydrotherapy", "Advanced Lab Panels", "Teeth Whitening Services"],
  isPopular: false,
  buttonText: "Join Membership",
  buttonLink: "/Memberships"
}, {
  name: "Protocol Elite",
  priceMonthly: 349,
  priceAnnual: 279,
  description: "Our signature tier for comprehensive health optimization.",
  features: ["2 Monthly Vitamin Injections", "1 Monthly IV Therapy Session", "Unlimited Sauna Access", "20% Off All Lab Testing", "Dedicated Wellness Concierge", "Advanced Bio-Mat Sessions", "Oxygen Bar Access", "Quarterly Health Reviews", "Teeth Whitening Services"],
  notIncluded: [],
  isPopular: true,
  buttonText: "Join Membership",
  buttonLink: "/Memberships"
}, {
  name: "The M / O Protocol",
  priceMonthly: 599,
  priceAnnual: 479,
  description: "The ultimate luxury experience for total body transformation.",
  features: ["Weekly Vitamin Injections", "2 Monthly IV Therapy Sessions", "Unlimited Sauna & Wellness Rooms", "Monthly Colon Hydrotherapy", "VIP 24/7 Medical Support", "Comprehensive Annual Lab Panel", "Private Wellness Suite", "Unlimited Oxygen Bar", "Complimentary Guest Passes", "Teeth Whitening Services"],
  notIncluded: [],
  isPopular: false,
  buttonText: "Join Membership",
  buttonLink: "/Memberships"
}];
export default function ChooseYourPackage() {
  const [isAnnual, setIsAnnual] = useState(false);
  const {
    headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll();
  const {
    cardsRef,
    isVisible: areCardsVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="224509" id="membership-tiers" className="bg-background text-foreground py-24 overflow-hidden">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        {}
        <div ref={headerRef} className={`flex flex-col items-center text-center max-w-3xl mx-auto mb-16 transition-all duration-700 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="text-3xl md:text-5xl font-bold font-default tracking-tight mb-6 text-primary">
            Membership Tiers
          </h2>
          <p className="text-lg text-muted-foreground mb-8 max-w-2xl font-default">
            Invest in your longevity with curated wellness protocols. 
            No long-term commitments, just a dedicated path to your highest state of being.
          </p>

          {}
          <div className="flex items-center gap-4 bg-secondary p-2 rounded-full border border-border">
            <span className={`text-sm font-medium transition-colors font-default ${!isAnnual ? "text-primary" : "text-muted-foreground"}`}>
              Monthly
            </span>
            <Switch checked={isAnnual} onCheckedChange={setIsAnnual} className="data-[state=checked]:bg-primary" />
            <span className={`text-sm font-medium transition-colors flex items-center gap-2 font-default ${isAnnual ? "text-primary" : "text-muted-foreground"}`}>
              Annually
              <Badge variant="secondary" className="bg-primary/10 text-primary hover:bg-primary/20 border-none font-default">
                Save 20%
              </Badge>
            </span>
          </div>
        </div>

        {}
        <div ref={cardsRef} className={`grid grid-cols-1 md:grid-cols-3 gap-8 items-center transition-all duration-1000 ease-out ${areCardsVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          {plans.map((plan, i) => {
          const price = isAnnual ? plan.priceAnnual : plan.priceMonthly;
          return <div key={plan.name} data-index={i} className={`relative flex flex-col p-8 rounded-xl bg-card border transition-all duration-300 ${plan.isPopular ? "border-[var(--color-green-accent)] shadow-xl md:-translate-y-4 md:scale-105 z-10" : "border-border shadow-sm hover:shadow-md"}`}>
                {plan.isPopular && <div className="absolute -top-4 left-0 right-0 flex justify-center">
                    <Badge className="bg-chart-2 text-white hover:bg-chart-2/90 px-4 py-1 text-sm rounded-full shadow-md font-default">
                      Recommended
                    </Badge>
                  </div>}

                <div className="mb-8">
                  <h3 className="text-xl font-semibold font-default mb-2 text-primary">{plan.name}</h3>
                  <p className="text-sm text-muted-foreground min-h-[40px] font-default">
                    {plan.description}
                  </p>
                </div>

                <div className="mb-8 flex items-baseline text-primary">
                  <span className="text-5xl font-bold font-default tracking-tight">
                    ${price}
                  </span>
                  <span className="text-xl font-medium ml-1 font-default"></span>
                  <span className="text-muted-foreground ml-1 font-default">/mo</span>
                </div>

                <div className="flex-grow">
                  <ul className="space-y-4 mb-8">
                    {plan.features.map((feature, idx) => <li key={`feat-${idx}`} data-index={idx} className="flex items-start gap-3">
                        <Check className={`size-5 shrink-0 ${plan.isPopular ? "text-chart-2" : "text-primary"}`} />
                        <span className="text-sm text-foreground font-default">{feature}</span>
                      </li>)}
                    {plan.notIncluded.map((feature, idx) => <li key={`not-feat-${idx}`} data-index={idx} className="flex items-start gap-3 opacity-40">
                        <X className="size-5 shrink-0 text-muted-foreground" />
                        <span className="text-sm text-muted-foreground font-default">{feature}</span>
                      </li>)}
                  </ul>
                </div>

                <Button asChild size="lg" className={`w-full rounded-md font-semibold transition-all font-default tracking-widest uppercase text-xs bg-[var(--color-green-accent)] text-white hover:bg-[var(--color-green-accent)]/90 ${plan.isPopular ? "shadow-lg scale-[1.02]" : ""}`}>
                  <Link to={plan.buttonLink}>{plan.buttonText}</Link>
                </Button>
              </div>;
        })}
        </div>
      </div>
    </section>;
}